import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FileDropZoneComponent } from 'src/app/shared/components/file-drop-zone/file-drop-zone.component';
import { DIALOG_BUTTONS, TRAVEL_REQUEST_TEMPLATE_LABEL_KEYS } from 'src/app/shared/constants';
import { DialogComponent } from 'src/app/shared/dialogs/dialog/dialog.component';
import { TravelRequestTemplateType } from 'src/app/shared/enums/travel-request-template-type.enum';
import { ITravelRequestTemplateStatus } from 'src/app/shared/models/travel-request.model';
import { AlertService, AlertType } from 'src/app/shared/services';
import { TravelRequestService } from 'src/app/shared/services/travel-request.service';

interface TemplateRow {
  type: TravelRequestTemplateType;
  sectionLabelKey: string;
  dropHintKey: string;
  status: ITravelRequestTemplateStatus | null;
  uploading: boolean;
  deleting: boolean;
  downloading: boolean;
}

const CATEGORIES = [TravelRequestTemplateType.BULGARIA, TravelRequestTemplateType.ABROAD];

const DROP_HINT_KEYS: Record<TravelRequestTemplateType, string> = {
  [TravelRequestTemplateType.BULGARIA]: 'ADMIN.TRAVEL_REQUESTS.TEMPLATES.BULGARIA_DROP_HINT',
  [TravelRequestTemplateType.ABROAD]: 'ADMIN.TRAVEL_REQUESTS.TEMPLATES.ABROAD_DROP_HINT',
};

@Component({
  selector: 'app-upload-travel-request-templates-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
    FileDropZoneComponent,
  ],
  templateUrl: './upload-travel-request-templates-dialog.component.html',
  styleUrl: './upload-travel-request-templates-dialog.component.scss',
})
export class UploadTravelRequestTemplatesDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<UploadTravelRequestTemplatesDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly travelRequestService = inject(TravelRequestService);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly buttons = DIALOG_BUTTONS;
  protected readonly docxAccept =
    '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  protected readonly isLoading = signal(true);

  private readonly statusesByType = signal<
    Partial<Record<TravelRequestTemplateType, ITravelRequestTemplateStatus>>
  >({});
  private readonly uploadingByType = signal<Partial<Record<TravelRequestTemplateType, boolean>>>(
    {},
  );
  private readonly deletingByType = signal<Partial<Record<TravelRequestTemplateType, boolean>>>({});
  private readonly downloadingByType = signal<Partial<Record<TravelRequestTemplateType, boolean>>>(
    {},
  );

  protected readonly rows = computed<TemplateRow[]>(() => {
    const statuses = this.statusesByType();
    const uploading = this.uploadingByType();
    const deleting = this.deletingByType();
    const downloading = this.downloadingByType();
    return CATEGORIES.map((type) => ({
      type,
      sectionLabelKey: TRAVEL_REQUEST_TEMPLATE_LABEL_KEYS[type],
      dropHintKey: DROP_HINT_KEYS[type],
      status: statuses[type] ?? null,
      uploading: uploading[type] ?? false,
      deleting: deleting[type] ?? false,
      downloading: downloading[type] ?? false,
    }));
  });

  constructor() {
    this.loadStatuses();
  }

  protected onFilesAdded(type: TravelRequestTemplateType, files: File[]): void {
    const file = files[0];
    if (!file) {
      return;
    }
    if (this.hasCyrillicCharacters(file.name)) {
      this.alertService.showAlert(
        this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.UPLOAD_CYRILLIC_FILENAME_ERROR'),
        AlertType.error,
      );
      return;
    }
    this.uploadingByType.update((current) => ({ ...current, [type]: true }));
    this.travelRequestService
      .uploadTemplate(type, file, TravelRequestService.TRAVEL_MANAGEMENT_BASE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.alertService.showAlert(
            this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.UPLOAD_SUCCESS'),
            AlertType.success,
          );
          this.loadStatuses();
        },
        error: (error: HttpErrorResponse) => this.handleUploadError(type, error),
      });
  }

  protected downloadTemplate(row: TemplateRow): void {
    this.downloadingByType.update((current) => ({ ...current, [row.type]: true }));
    this.travelRequestService
      .downloadTemplate(row.type, TravelRequestService.TRAVEL_MANAGEMENT_BASE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.downloadingByType.update((current) => ({ ...current, [row.type]: false }));
        },
        error: (error: HttpErrorResponse) => {
          this.downloadingByType.update((current) => ({ ...current, [row.type]: false }));
          const backendMessage = typeof error.error === 'string' ? error.error.trim() : '';
          this.alertService.showAlert(
            backendMessage ||
              this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.DOWNLOAD_ERROR'),
            AlertType.error,
          );
        },
      });
  }

  protected deleteTemplate(row: TemplateRow): void {
    this.dialog
      .open(DialogComponent, {
        data: {
          title: this.translate.instant(
            'ADMIN.TRAVEL_REQUESTS.TEMPLATES.DELETE_CONFIRMATION_TITLE',
          ),
          description: this.translate.instant(
            'ADMIN.TRAVEL_REQUESTS.TEMPLATES.DELETE_CONFIRMATION_DESCRIPTION',
            { template: this.translate.instant(row.sectionLabelKey) },
          ),
          sharedButtonClass: this.buttons.deleteButton,
          sharedButtonText: this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.DELETE'),
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.performDelete(row.type);
        }
      });
  }

  protected close(): void {
    this.dialogRef.close();
  }

  private loadStatuses(): void {
    this.isLoading.set(true);
    this.travelRequestService
      .getTemplateStatuses(TravelRequestService.TRAVEL_MANAGEMENT_BASE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (statuses) => {
          const map: Partial<Record<TravelRequestTemplateType, ITravelRequestTemplateStatus>> = {};
          statuses.forEach((status) => (map[status.type] = status));
          this.statusesByType.set(map);
          this.uploadingByType.set({});
          this.deletingByType.set({});
          this.downloadingByType.set({});
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.alertService.showAlert(
            this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.LOAD_ERROR'),
            AlertType.error,
          );
        },
      });
  }

  private hasCyrillicCharacters(fileName: string): boolean {
    return /[Ѐ-ӿ]/.test(fileName);
  }

  private handleUploadError(type: TravelRequestTemplateType, error: HttpErrorResponse): void {
    this.uploadingByType.update((current) => ({ ...current, [type]: false }));
    const backendMessage = typeof error.error === 'string' ? error.error.trim() : '';
    this.alertService.showAlert(
      backendMessage || this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.UPLOAD_ERROR'),
      AlertType.error,
    );
  }

  private performDelete(type: TravelRequestTemplateType): void {
    this.deletingByType.update((current) => ({ ...current, [type]: true }));
    this.travelRequestService
      .deleteTemplate(type, TravelRequestService.TRAVEL_MANAGEMENT_BASE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.alertService.showAlert(
            this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.DELETE_SUCCESS'),
            AlertType.success,
          );
          this.loadStatuses();
        },
        error: (error: HttpErrorResponse) => {
          this.deletingByType.update((current) => ({ ...current, [type]: false }));
          const backendMessage = typeof error.error === 'string' ? error.error.trim() : '';
          this.alertService.showAlert(
            backendMessage ||
              this.translate.instant('ADMIN.TRAVEL_REQUESTS.TEMPLATES.DELETE_ERROR'),
            AlertType.error,
          );
        },
      });
  }
}
